import React, {useEffect, useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {
	ButtonDropdown,
	DropdownList,
	FlexColumn,
	FlexRow,
	Icon,
	Label,
	Modal,
	SearchField,
	SubHeading,
	Table,
	Text,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import type {AddBillToEntityModal_viewer$data as AddBillToEntityModal_viewer} from './__generated__/AddBillToEntityModal_viewer.graphql';
import Util from '../../../shared/util/util';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import CreateBulkBillToMutation from '../../../../mutations/invoicing/CreateBulkBillToMutation';
import {Contact, filterContactsByIntegrationInstanceId} from '../InvoiceIntegrationsUtils';
import {useEnabledIntegrationContacts} from '../useEnabledIntegrationContacts';
import InvoicingIntegrationIcon from '../InvoicingIntegrationIcon';

type BillFrom = NonNullable<NonNullable<AddBillToEntityModal_viewer['company']>['billFroms']>[number];

type AddBillToEntityModalProps = {
	billFromId: string;
	closeModal: () => void;
	viewer: AddBillToEntityModal_viewer;
};

const AddBillToEntityModal = ({billFromId, closeModal, viewer}: AddBillToEntityModalProps) => {
	const {formatMessage} = useIntl();
	const [searchValue, setSearchValue] = useState('');
	const [contacts, setContacts] = useState<Contact[]>([]);
	const billFroms = viewer.company?.billFroms || [];
	const [billFrom, setBillFrom] = useState(billFroms.find(billFrom => billFrom?.id === billFromId));
	const alreadyImported = useMemo(() => new Set(billFrom?.billTos.edges?.map(edge => edge?.node?.externalId)), [billFrom]);
	const [selectedRows, setSelectedRows] = useState<Contact[]>([]);
	const [filteredRows, setFilteredRows] = useState<Contact[]>([]);
	const filteredContactsCount = contacts.length - filteredRows.length;
	const {contacts: initialContactsList, isLoading} = useEnabledIntegrationContacts(viewer.company, closeModal);

	useEffect(() => {
		if (!isLoading) {
			const filteredContacts = filterContactsByIntegrationInstanceId(initialContactsList, billFrom);
			setContacts(filteredContacts);
			setFilteredRows(filteredContacts);
		}
	}, [initialContactsList, billFrom, isLoading]);

	const importBillTos = () => {
		if (selectedRows.length) {
			Util.CommitMutation(
				CreateBulkBillToMutation,
				{
					billTos: selectedRows.map(row => ({
						name: row.name,
						address: row.address,
						taxId: row.taxId,
						externalId: row.id,
						billFromId: billFrom?.id,
					})),
				},
				closeModal
			);
		} else {
			closeModal();
		}
	};

	const calculateNoOfCustomers = (billFrom: BillFrom | undefined) => {
		return isLoading ? '' : `(${filterContactsByIntegrationInstanceId(initialContactsList, billFrom).length})`;
	};

	const BillFromItem = ({billFrom}: {billFrom: BillFrom | undefined}) => (
		<FlexRow gap={'s'}>
			<InvoicingIntegrationIcon integration={billFrom?.integration} />
			<div>
				{billFrom?.name} {calculateNoOfCustomers(billFrom)}
			</div>
		</FlexRow>
	);

	const alreadyImportedIcon = {
		label: formatMessage({id: 'settings_finance.bill_to.already_added'}),
		icon: 'checkCircle',
		color: 'success',
	};
	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="settings_finance.add_bill_to_entities" />
			</Modal.Title>
			<Modal.Content>
				<SearchField
					isExpandable={false}
					value={searchValue}
					onChange={setSearchValue}
					placeholder={formatMessage({id: 'common.search'})}
				/>

				{billFroms.length === 1 ? (
					<Label>
						<BillFromItem billFrom={billFrom} />
					</Label>
				) : (
					<DropdownList>
						<DropdownList.Trigger>
							<ButtonDropdown emphasis={'medium'} style={{width: 'fit-content'}}>
								<BillFromItem billFrom={billFrom} />
							</ButtonDropdown>
						</DropdownList.Trigger>
						<DropdownList.Content offset={5} align={'start'}>
							{billFroms.map(current => (
								<DropdownList.Item key={current?.id} onClick={() => setBillFrom(current)}>
									<FlexRow>
										<BillFromItem billFrom={current} />
										{current?.id === billFrom?.id && <Icon icon={'iconTick'} />}
									</FlexRow>
								</DropdownList.Item>
							))}
						</DropdownList.Content>
					</DropdownList>
				)}

				{isLoading ? (
					<InlineLoader />
				) : (
					<>
						<div>
							<Table
								data={contacts}
								searchValue={searchValue}
								onSelectionChange={setSelectedRows}
								onFilteringChange={setFilteredRows}
								enableRowSelection={row => !alreadyImported.has(row.original.id)}
								width={'100%'}
							>
								<Table.TextColumn header={formatMessage({id: 'common.select_all'})} accessorKey={'name'} />
								<Table.IconColumn
									accessorKey={'id'}
									header={'Status'}
									hideHeader
									align={Table.Align.right}
									icon={(value: string) => (alreadyImported.has(value) ? alreadyImportedIcon : undefined)}
								/>
							</Table>
						</div>
						<FlexColumn alignItems={'center'}>
							{filteredRows.length === 0 && (
								<SubHeading size={'2'}>
									<FormattedMessage id={'settings_finance.bill_to.no_results'} />
								</SubHeading>
							)}
							{filteredContactsCount > 0 && (
								<Text>
									<FormattedMessage
										id={'settings_finance.bill_to.filtering_count'}
										values={{count: filteredContactsCount}}
									/>
								</Text>
							)}
						</FlexColumn>
					</>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={importBillTos} disabled={!selectedRows.length}>
					<FormattedMessage id={'settings_finance.bill_to.add_selected'} values={{count: selectedRows.length}} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.close'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const AddBillToEntityModalQuery = graphql`
	query AddBillToEntityModalQuery {
		viewer {
			actualPersonId
			component(name: "AddBillToEntityModal")
			...AddBillToEntityModal_viewer
		}
	}
`;

export default createFragmentContainer(AddBillToEntityModal, {
	viewer: graphql`
		fragment AddBillToEntityModal_viewer on Viewer {
			id
			company {
				id
				newXeroEnabled
				economicEnabled
				quickbooksEnabled
				billFroms {
					id
					integration
					externalId
					name
					billTos(first: 10000) @connection(key: "BillFrom_billTos", filters: []) {
						edges {
							node {
								id
								name
								address
								taxId
								externalId
							}
						}
					}
				}
			}
		}
	`,
});
